<template>
  <div class="rounded-md border border-obsidian-200 bg-white focus-within:border-obsidian-400">
    <div v-if="editor" class="flex flex-wrap gap-1 border-b border-obsidian-200 p-1">
      <CVButton
        v-for="action in toolbarActions"
        :key="action.label"
        type="button"
        variant="ghost"
        size="iconXs"
        :class="{ 'bg-obsidian-100': action.isActive() }"
        :aria-label="action.label"
        :title="action.label"
        @click="action.run()"
      >
        <component :is="action.icon" class="size-4" />
      </CVButton>
    </div>
    <EditorContent v-if="editor" :editor="editor" class="rich-text-editor prose max-w-none p-3" />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, shallowRef, watch } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import {
  BoldIcon,
  Heading1Icon,
  Heading2Icon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  PilcrowIcon,
  Redo2Icon,
  Undo2Icon,
} from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';

/** Rich text editor that reads and writes HTML. */
const model = defineModel<string | null>({ default: '' });

const editor = shallowRef<Editor | undefined>(
  new Editor({
    content: model.value ?? '',
    extensions: [StarterKit],
    onUpdate: ({ editor: updatedEditor }) => {
      model.value = updatedEditor.isEmpty ? '' : updatedEditor.getHTML();
    },
  }),
);

watch(model, (value) => {
  if (editor.value && value !== editor.value.getHTML()) {
    editor.value.commands.setContent(value ?? '', { emitUpdate: false });
  }
});

const chain = () => editor.value!.chain().focus();

const toolbarActions = [
  {
    label: 'H1',
    icon: Heading1Icon,
    run: () => chain().toggleHeading({ level: 1 }).run(),
    isActive: () => !!editor.value?.isActive('heading', { level: 1 }),
  },
  {
    label: 'H2',
    icon: Heading2Icon,
    run: () => chain().toggleHeading({ level: 2 }).run(),
    isActive: () => !!editor.value?.isActive('heading', { level: 2 }),
  },
  {
    label: 'Paragraph',
    icon: PilcrowIcon,
    run: () => chain().setParagraph().run(),
    isActive: () => !!editor.value?.isActive('paragraph'),
  },
  {
    label: 'Bold',
    icon: BoldIcon,
    run: () => chain().toggleBold().run(),
    isActive: () => !!editor.value?.isActive('bold'),
  },
  {
    label: 'Italic',
    icon: ItalicIcon,
    run: () => chain().toggleItalic().run(),
    isActive: () => !!editor.value?.isActive('italic'),
  },
  {
    label: 'Bullet list',
    icon: ListIcon,
    run: () => chain().toggleBulletList().run(),
    isActive: () => !!editor.value?.isActive('bulletList'),
  },
  {
    label: 'Numbered list',
    icon: ListOrderedIcon,
    run: () => chain().toggleOrderedList().run(),
    isActive: () => !!editor.value?.isActive('orderedList'),
  },
  { label: 'Undo', icon: Undo2Icon, run: () => chain().undo().run(), isActive: () => false },
  { label: 'Redo', icon: Redo2Icon, run: () => chain().redo().run(), isActive: () => false },
];

onBeforeUnmount(() => {
  editor.value?.destroy();
});
</script>

<style scoped>
.rich-text-editor :deep(.ProseMirror) {
  min-height: 16rem;
  outline: none;
}
</style>
